import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock, createVNode as _createVNode, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-df0ec4da"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "project-carousel__image-wrap" }
const _hoisted_2 = ["src", "alt"]
const _hoisted_3 = ["aria-label"]
const _hoisted_4 = ["aria-label", "aria-current", "onClick"]

import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";

const AUTOPLAY_INTERVAL = 3500;
const TRANSITION_DURATION = 500;
const RESET_SETTLE_DELAY = 100;

interface SwipeInstance {
  swipeTo: (index: number) => void;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ProjectCarousel',
  props: {
    slides: {},
    altPrefix: {}
  },
  setup(__props: any) {

const props = __props;

const { locale } = useI18n();
const swipeRef = ref<SwipeInstance | null>(null);
const currentIndex = ref(0);
const isPaused = ref(false);
const isResetting = ref(false);
const isDragging = ref(false);
let autoplayTimer: number | undefined;
let resetTimer: number | undefined;

const renderedSlides = computed(() => {
  if (props.slides.length <= 1) return props.slides;

  return [
    props.slides[props.slides.length - 1],
    ...props.slides,
    props.slides[0],
  ];
});

const dotsLabel = computed(() =>
  locale.value === "zh" ? "图片切换控制器" : "Image carousel controls"
);
const switchLabel = computed(() =>
  locale.value === "zh" ? "切换到图片" : "Go to image"
);
const imageLabel = computed(() =>
  locale.value === "zh" ? "图片" : "image"
);

const toLogicalIndex = (trackIndex: number) => {
  const count = props.slides.length;
  if (count <= 1) return 0;
  return (trackIndex - 1 + count) % count;
};

const clearAutoplay = () => {
  if (autoplayTimer !== undefined) {
    window.clearTimeout(autoplayTimer);
    autoplayTimer = undefined;
  }
};

const clearReset = () => {
  if (resetTimer !== undefined) {
    window.clearTimeout(resetTimer);
    resetTimer = undefined;
  }
};

const scheduleAutoplay = () => {
  clearAutoplay();
  if (
    isPaused.value ||
    isDragging.value ||
    isResetting.value ||
    resetTimer !== undefined ||
    props.slides.length <= 1
  ) {
    return;
  }

  autoplayTimer = window.setTimeout(() => {
    autoplayTimer = undefined;
    swipeRef.value?.swipeTo(currentIndex.value + 2);
  }, AUTOPLAY_INTERVAL);
};

const resetFromClone = (trackIndex: number) => {
  clearAutoplay();
  clearReset();

  resetTimer = window.setTimeout(async () => {
    resetTimer = undefined;
    isResetting.value = true;
    await nextTick();

    const targetIndex = trackIndex === 0 ? props.slides.length : 1;
    swipeRef.value?.swipeTo(targetIndex);

    resetTimer = window.setTimeout(() => {
      resetTimer = undefined;
      isResetting.value = false;
      scheduleAutoplay();
    }, RESET_SETTLE_DELAY);
  }, TRANSITION_DURATION);
};

const handleTrackChange = (trackIndex: number) => {
  currentIndex.value = toLogicalIndex(trackIndex);

  const cloneAtStart = trackIndex === 0;
  const cloneAtEnd = trackIndex === props.slides.length + 1;
  if (props.slides.length > 1 && (cloneAtStart || cloneAtEnd)) {
    resetFromClone(trackIndex);
    return;
  }

  scheduleAutoplay();
};

const handleDragStart = () => {
  isDragging.value = true;
  clearAutoplay();
};

const handleDragEnd = () => {
  isDragging.value = false;
  scheduleAutoplay();
};

const goTo = (index: number) => {
  clearReset();
  isResetting.value = false;
  swipeRef.value?.swipeTo(index + 1);
};

watch(isPaused, (paused) => {
  if (paused) clearAutoplay();
  else scheduleAutoplay();
});

onMounted(scheduleAutoplay);
onBeforeUnmount(() => {
  clearAutoplay();
  clearReset();
});

return (_ctx: any,_cache: any) => {
  const _component_van_swipe_item = _resolveComponent("van-swipe-item")!
  const _component_van_swipe = _resolveComponent("van-swipe")!

  return (_openBlock(), _createElementBlock("div", {
    class: "project-carousel",
    onMouseenter: _cache[0] || (_cache[0] = ($event: any) => (isPaused.value = true)),
    onMouseleave: _cache[1] || (_cache[1] = ($event: any) => (isPaused.value = false)),
    onFocusin: _cache[2] || (_cache[2] = ($event: any) => (isPaused.value = true)),
    onFocusout: _cache[3] || (_cache[3] = ($event: any) => (isPaused.value = false))
  }, [
    _createVNode(_component_van_swipe, {
      ref_key: "swipeRef",
      ref: swipeRef,
      class: "project-carousel__swipe",
      duration: isResetting.value ? 0 : TRANSITION_DURATION,
      "initial-swipe": _ctx.slides.length > 1 ? 1 : 0,
      loop: false,
      "show-indicators": false,
      "stop-propagation": false,
      onChange: handleTrackChange,
      onDragStart: handleDragStart,
      onDragEnd: handleDragEnd
    }, {
      default: _withCtx(() => [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(renderedSlides.value, (slide, index) => {
          return (_openBlock(), _createBlock(_component_van_swipe_item, {
            key: `${slide}-${index}`
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_1, [
                _createElementVNode("img", {
                  class: "project-carousel__image",
                  src: slide,
                  alt: `${_ctx.altPrefix} - ${imageLabel.value} ${toLogicalIndex(index) + 1}`,
                  draggable: "false"
                }, null, 8, _hoisted_2)
              ])
            ]),
            _: 2
          }, 1024))
        }), 128))
      ]),
      _: 1
    }, 8, ["duration", "initial-swipe"]),
    _createElementVNode("div", {
      class: "project-carousel__dots",
      "aria-label": dotsLabel.value
    }, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.slides, (_, index) => {
        return (_openBlock(), _createElementBlock("button", {
          key: index,
          type: "button",
          class: _normalizeClass(["project-carousel__dot", { 'is-active': currentIndex.value === index }]),
          "aria-label": `${switchLabel.value} ${index + 1}`,
          "aria-current": currentIndex.value === index ? 'true' : undefined,
          onClick: ($event: any) => (goTo(index))
        }, null, 10, _hoisted_4))
      }), 128))
    ], 8, _hoisted_3)
  ], 32))
}
}

})